"use client";

import { blockedEmailError } from "@/lib/emailPolicy";
import { useEffect, useState, type FormEvent } from "react";
import { BookOpen, Download, ExternalLink, FileText, Loader2, PlayCircle, type LucideIcon } from "lucide-react";
import { API_URL } from "@/lib/apiUrl";
import { type MaterialKind, type SiteMaterial } from "@/lib/materials";
import { readSession } from "@/lib/portal/session";
import { Modal } from "@/components/portal/Modal";
import { PhoneInput, validatePhone } from "@/components/ui/PhoneInput";
import { LegalModalLink } from "@/components/legal/LegalModalLink";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const KIND: Record<MaterialKind, { label: string; icon: LucideIcon; action: string }> = {
  brochure: { label: "Brochure", icon: FileText, action: "Download" },
  ebook: { label: "E-book", icon: BookOpen, action: "Download" },
  video: { label: "Video", icon: PlayCircle, action: "Watch" },
  other: { label: "Document", icon: FileText, action: "Download" },
};

type Grant = { url: string } | "access-required";

/** Asks the server where to open a material. Signed-in portal users and visitors with a pass get it straight away. */
async function requestGrant(id: number, access = ""): Promise<Grant> {
  const token = readSession()?.token;
  const res = await fetch(`${API_URL}/course-materials/${id}/grant`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ access }),
  });
  const json = (await res.json().catch(() => ({}))) as { code?: string; message?: string; data?: { url: string } };
  if (res.status === 401 && json.code === "ACCESS_REQUIRED") return "access-required";
  if (!res.ok || !json.data?.url) throw new Error(json.message || "Could not open this material. Please try again.");
  return { url: json.data.url };
}

/** "Brochure / E-book" button for the course hero: downloads the course's file, asking visitors for their details first. */
export function CourseMaterialButton({ materials, category, course, className }: { materials: SiteMaterial[]; category?: string; course?: string; className?: string }) {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState<SiteMaterial | null>(null);
  const [error, setError] = useState("");
  const m = materials[0];
  if (!m) return null;

  async function open(target: SiteMaterial, access = "") {
    setError("");
    setBusy(true);
    try {
      const grant = await requestGrant(target.id, access);
      if (grant === "access-required") setAsking(target);
      else window.location.assign(grant.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open this file.");
    } finally {
      setBusy(false);
    }
  }

  // visitors who are not signed in fill the form every time; the pass only covers this one download
  async function onUnlocked(pass: string) {
    setAsking(null);
    await open(m, pass);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => open(m)}
        disabled={busy}
        className={className ?? "inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-brand-700 bg-white px-6 text-[15px] font-semibold text-brand-700 transition-colors hover:bg-brand-600/5 disabled:opacity-70"}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Brochure / E-book
      </button>
      {error && (
        <p role="alert" className="w-full text-sm font-medium text-brand-700">
          {error}
        </p>
      )}
      <AccessForm material={asking} category={category} course={course} onClose={() => setAsking(null)} onDone={onUnlocked} />
    </>
  );
}

export function CourseMaterials({ materials, category, course }: { materials: SiteMaterial[]; category?: string; course?: string }) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [asking, setAsking] = useState<SiteMaterial | null>(null);
  const [error, setError] = useState("");

  if (materials.length === 0) return null;

  async function open(m: SiteMaterial, access = "") {
    setError("");
    setBusyId(m.id);
    // files download in place; only videos and links open a tab (opened inside the click so pop-up blockers allow it)
    const download = !m.isLink && m.kind !== "video";
    const tab = download ? null : window.open("", "_blank");
    try {
      const grant = await requestGrant(m.id, access);
      if (grant === "access-required") {
        tab?.close();
        setAsking(m);
        return;
      }
      if (tab) tab.location.replace(grant.url);
      else window.location.assign(grant.url);
    } catch (e) {
      tab?.close();
      setError(e instanceof Error ? e.message : "Could not open this material.");
    } finally {
      setBusyId(null);
    }
  }

  // after the form: remember the pass, then open what they asked for
  async function onUnlocked(pass: string) {
    const m = asking;
    setAsking(null);
    if (m) await open(m, pass);
  }

  return (
    <section>
      <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">Course material</h2>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-soft">
        Brochures, e-books and guides for this programme. Sign in, or share your details once, to open them.
      </p>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {materials.map((m) => {
          const k = KIND[m.kind] ?? KIND.other;
          const Icon = k.icon;
          return (
            <li key={m.id} className="flex items-start gap-4 rounded-[var(--radius-card)] border border-line bg-white p-5 shadow-soft">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-600/8 text-brand-700">
                <Icon className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-700">
                  {k.label}
                </div>
                <h3 className="mt-1 font-display text-lg font-semibold leading-snug text-ink">{m.title}</h3>
                {m.description && <p className="mt-1 text-sm leading-relaxed text-ink-soft">{m.description}</p>}
                <button
                  type="button"
                  onClick={() => open(m)}
                  disabled={busyId === m.id}
                  className="mt-3 inline-flex h-9 items-center gap-2 rounded-full bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-70"
                >
                  {busyId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : m.isLink ? <ExternalLink className="h-4 w-4" /> : k.action === "Watch" ? <PlayCircle className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                  {m.isLink && k.action === "Download" ? "Open" : k.action}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {error && (
        <p role="alert" className="mt-3 text-sm font-medium text-brand-700">
          {error}
        </p>
      )}

      <AccessForm material={asking} category={category} course={course} onClose={() => setAsking(null)} onDone={onUnlocked} />
    </section>
  );
}

/** The details form shown to visitors who are not signed in - same fields as the Contact Us enquiry. */
function AccessForm({ material, category, course, onClose, onDone }: { material: SiteMaterial | null; category?: string; course?: string; onClose: () => void; onDone: (pass: string) => void }) {
  const [v, setV] = useState({ name: "", email: "", phone: "", city: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  // start fresh every time the form closes (dismissed or after a successful download)
  useEffect(() => {
    if (material) return;
    setV({ name: "", email: "", phone: "", city: "" });
    setConsent(false);
    setErrors({});
    setFormError("");
  }, [material]);

  const set = (k: keyof typeof v) => (val: string) => {
    setV((p) => ({ ...p, [k]: val }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!material || busy) return;
    const errs: Record<string, string> = {};
    if (!v.name.trim()) errs.name = "Name is required";
    if (!emailRe.test(v.email.trim())) errs.email = "Enter a valid email address";
    else if (blockedEmailError(v.email)) errs.email = blockedEmailError(v.email);
    const phoneProblem = validatePhone(v.phone);
    if (phoneProblem) errs.phone = phoneProblem;
    if (!v.city.trim()) errs.city = "City is required";
    if (!consent) errs.consent = "Please accept the privacy notice to continue";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    setFormError("");
    try {
      const res = await fetch(`${API_URL}/course-materials/${material.id}/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...v, consent: "yes" }),
      });
      const json = (await res.json().catch(() => ({}))) as { message?: string; data?: { access?: string } };
      if (!res.ok || !json.data?.access) throw new Error(json.message || "Could not save your details. Please try again.");
      onDone(json.data.access);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Could not save your details. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const input = (err?: string) =>
    `h-11 w-full rounded-xl border bg-white px-3.5 text-sm text-ink outline-none transition focus:ring-2 ${
      err ? "border-brand-500 focus:border-brand-500 focus:ring-brand-600/20" : "border-line focus:border-brand-500 focus:ring-brand-600/15"
    }`;
  const label = (text: string) => <span className="mb-1.5 block text-sm font-medium text-ink">{text}<span className="text-brand-600"> *</span></span>;
  const err = (k: string) => (errors[k] ? <p className="mt-1 text-xs text-brand-600">{errors[k]}</p> : null);

  return (
    <Modal
      open={!!material}
      onClose={() => !busy && onClose()}
      title="Almost there!"
      description={material ? `Tell us a little about yourself and “${material.title}” will be ${material.kind === "video" || material.isLink ? "ready to watch" : "downloaded"} right away.` : undefined}
      size="md"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <div>
          <label htmlFor="mat-name">{label("Full name")}</label>
          <input id="mat-name" autoComplete="name" maxLength={120} value={v.name} onChange={(e) => set("name")(e.target.value)} className={input(errors.name)} />
          {err("name")}
        </div>
        <div>
          <label htmlFor="mat-email">{label("Email")}</label>
          <input id="mat-email" type="email" autoComplete="email" maxLength={190} value={v.email} onChange={(e) => set("email")(e.target.value)} className={input(errors.email)} />
          {err("email")}
        </div>
        <div>
          <label htmlFor="mat-phone">{label("Mobile number")}</label>
          <PhoneInput id="mat-phone" value={v.phone} error={errors.phone} onChange={set("phone")} />
          {err("phone")}
        </div>
        <div>
          <label htmlFor="mat-city">{label("City")}</label>
          <input id="mat-city" autoComplete="address-level2" maxLength={100} value={v.city} onChange={(e) => set("city")(e.target.value)} className={input(errors.city)} />
          {err("city")}
        </div>
        {(category || course) && (
          <div className="grid gap-4 sm:grid-cols-2">
            {category && (
              <div>
                <label htmlFor="mat-category"><span className="mb-1.5 block text-sm font-medium text-ink">Interested Courses</span></label>
                <input id="mat-category" readOnly value={category} className="h-11 w-full rounded-xl border border-line bg-cream px-3.5 text-sm text-ink-soft outline-none" />
              </div>
            )}
            {course && (
              <div>
                <label htmlFor="mat-course"><span className="mb-1.5 block text-sm font-medium text-ink">Course</span></label>
                <input id="mat-course" readOnly value={course} className="h-11 w-full rounded-xl border border-line bg-cream px-3.5 text-sm text-ink-soft outline-none" />
              </div>
            )}
          </div>
        )}
        <div>
          <label className="flex items-start gap-3 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                setErrors((p) => ({ ...p, consent: "" }));
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-line text-brand-600"
            />
            <span>
              I have read and agree to the{" "}
              <LegalModalLink doc="terms" className="font-medium text-brand-700 underline-offset-2 hover:underline">
                terms and conditions
              </LegalModalLink>{" "}
              laid by My Value Academy. See our{" "}
              <LegalModalLink doc="privacy" className="font-medium text-brand-700 underline-offset-2 hover:underline">
                privacy notice
              </LegalModalLink>
              .
            </span>
          </label>
          {err("consent")}
        </div>
        {formError && (
          <p role="alert" className="rounded-xl border border-brand-500/30 bg-brand-600/5 px-3.5 py-2.5 text-sm text-brand-700">
            {formError}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-6 text-[15px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-70"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {busy ? "Please wait…" : material && (material.kind === "video" || material.isLink) ? "Watch now" : "Download now"}
        </button>
      </form>
    </Modal>
  );
}
